import React, { useState } from 'react';
import { X, Users, Eye, Check, Copy, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import type { Wine, ShareAccessType } from '@/types';

interface ShareWinesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  wines: Wine[]; // Vini da condividere (pre-selezionati)
  onShare: (wineIds: string[], type: ShareAccessType) => Promise<string>; // Ritorna il codice
}

export const ShareWinesDialog: React.FC<ShareWinesDialogProps> = ({
  isOpen,
  onClose,
  wines,
  onShare,
}) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedWines, setSelectedWines] = useState<string[]>(wines.map(w => w.id));
  const [accessType, setAccessType] = useState<ShareAccessType>('read-only');
  const [shareCode, setShareCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Reset quando si chiude
  const handleClose = () => {
    setStep(1);
    setSelectedWines(wines.map(w => w.id));
    setAccessType('read-only');
    setShareCode(null);
    setCopied(false);
    onClose();
  };

  // Toggle selezione vino
  const toggleWine = (wineId: string) => {
    setSelectedWines(prev =>
      prev.includes(wineId)
        ? prev.filter(id => id !== wineId)
        : [...prev, wineId]
    );
  };

  // Genera codice condivisione
  const handleGenerateCode = async () => {
    if (selectedWines.length === 0) return;

    setLoading(true);
    try {
      const code = await onShare(selectedWines, accessType);
      setShareCode(code);
      setStep(3);
    } catch (error) {
      console.error('Error generating share code:', error);
      alert('Errore durante la generazione del codice di condivisione');
    } finally {
      setLoading(false);
    }
  };

  // Copia codice
  const handleCopyCode = async () => {
    if (!shareCode) return;

    try {
      await navigator.clipboard.writeText(shareCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Error copying code:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Condividi Vini</h2>
              <p className="text-sm text-gray-600 mt-1">
                Step {step} di 3
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* STEP 1: Seleziona vini */}
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Seleziona i vini da condividere
                  </h3>
                  <p className="text-sm text-gray-600">
                    Puoi selezionare uno o più vini da condividere
                  </p>
                </div>

                <div className="space-y-2">
                  {wines.map((wine) => (
                    <Card
                      key={wine.id}
                      padding="sm"
                      hover
                      onClick={() => toggleWine(wine.id)}
                      className="cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        {/* Checkbox */}
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            selectedWines.includes(wine.id)
                              ? 'bg-wine-purple border-wine-purple'
                              : 'border-gray-300'
                          }`}
                        >
                          {selectedWines.includes(wine.id) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>

                        {/* Wine Image */}
                        <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0">
                          {wine.imageUrls?.[0] || wine.imageUrl ? (
                            <img
                              src={wine.imageUrls?.[0] || wine.imageUrl}
                              alt={wine.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                              <Users className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                        </div>

                        {/* Wine Info */}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 truncate">
                            {wine.name}
                          </h4>
                          <p className="text-sm text-gray-600 truncate">
                            {wine.producer}
                            {wine.vintage && ` • ${wine.vintage}`}
                          </p>
                        </div>

                        {/* Quantity */}
                        {wine.quantity && wine.quantity > 1 && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
                            ×{wine.quantity}
                          </span>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>

                {selectedWines.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    Seleziona almeno un vino per continuare
                  </div>
                )}
              </div>
            )}

            {/* STEP 2: Tipo accesso */}
            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Scegli il tipo di accesso
                  </h3>
                  <p className="text-sm text-gray-600">
                    Seleziona {selectedWines.length === 1 ? 'il vino' : `i ${selectedWines.length} vini`} da condividere
                  </p>
                </div>

                {/* Access Type Options */}
                <div className="space-y-3">
                  {/* Read-only */}
                  <Card
                    padding="md"
                    hover
                    onClick={() => setAccessType('read-only')}
                    className={`cursor-pointer border-2 transition-colors ${
                      accessType === 'read-only'
                        ? 'border-wine-purple bg-wine-purple/5'
                        : 'border-transparent'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          accessType === 'read-only'
                            ? 'border-wine-purple'
                            : 'border-gray-300'
                        }`}
                      >
                        {accessType === 'read-only' && (
                          <div className="w-2.5 h-2.5 rounded-full bg-wine-purple" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Eye className="w-5 h-5 text-blue-600" />
                          <h4 className="font-semibold text-gray-900">Solo Lettura</h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          L'utente potrà vedere i vini ma non potrà modificarli, eliminarli o segnarli come bevuti
                        </p>
                      </div>
                    </div>
                  </Card>

                  {/* Full access */}
                  <Card
                    padding="md"
                    hover
                    onClick={() => setAccessType('full')}
                    className={`cursor-pointer border-2 transition-colors ${
                      accessType === 'full'
                        ? 'border-wine-purple bg-wine-purple/5'
                        : 'border-transparent'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          accessType === 'full'
                            ? 'border-wine-purple'
                            : 'border-gray-300'
                        }`}
                      >
                        {accessType === 'full' && (
                          <div className="w-2.5 h-2.5 rounded-full bg-wine-purple" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Users className="w-5 h-5 text-green-600" />
                          <h4 className="font-semibold text-gray-900">Accesso Completo</h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          L'utente avrà gli stessi privilegi: potrà modificare, eliminare e segnare i vini come bevuti
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            )}

            {/* STEP 3: Codice generato */}
            {step === 3 && shareCode && (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Codice di Condivisione Generato!
                  </h3>
                  <p className="text-sm text-gray-600">
                    Condividi questo codice con l'utente. Il codice scade tra 7 giorni.
                  </p>
                </div>

                {/* Share Code Display */}
                <Card padding="lg" className="bg-gray-50">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-3">Codice di Condivisione</p>
                    <div className="text-3xl font-mono font-bold text-wine-purple tracking-wider mb-4">
                      {shareCode}
                    </div>
                    <Button
                      icon={copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                      onClick={handleCopyCode}
                      variant={copied ? 'primary' : 'outline'}
                      fullWidth
                    >
                      {copied ? 'Copiato!' : 'Copia Codice'}
                    </Button>
                  </div>
                </Card>

                {/* Summary */}
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900">Riepilogo</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>• {selectedWines.length} {selectedWines.length === 1 ? 'vino condiviso' : 'vini condivisi'}</p>
                    <p>• Tipo accesso: <span className="font-medium">{accessType === 'full' ? 'Accesso Completo' : 'Solo Lettura'}</span></p>
                    <p>• Scadenza: 7 giorni</p>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-900">
                    <strong>Nota:</strong> I vini bevuti sono sempre personali e non verranno mai condivisi.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200">
            {step > 1 && step < 3 && (
              <Button
                variant="ghost"
                onClick={() => setStep((step - 1) as 1 | 2)}
              >
                Indietro
              </Button>
            )}
            {step === 3 && (
              <Button
                variant="ghost"
                onClick={handleClose}
              >
                Chiudi
              </Button>
            )}
            <div className="flex gap-3 ml-auto">
              {step < 3 && (
                <>
                  <Button variant="outline" onClick={handleClose}>
                    Annulla
                  </Button>
                  {step === 1 && (
                    <Button
                      onClick={() => setStep(2)}
                      disabled={selectedWines.length === 0}
                    >
                      Continua
                    </Button>
                  )}
                  {step === 2 && (
                    <Button
                      onClick={handleGenerateCode}
                      loading={loading}
                      icon={loading ? <Loader2 className="w-5 h-5 animate-spin" /> : undefined}
                    >
                      Genera Codice
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
